import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiArrowTopRightOnSquare } from "react-icons/hi2";
import toast from "react-hot-toast";

import SelectMonth from "../../components/selectMonth";
import SelectContact from "../../components/SelectContact";
import LoadingButton from "../../components/loadingButton";

import api from "../../services/api";

const Activity = ({ businessUnit }) => {
  const [date, setDate] = useState(null);
  const [contract, setContarct] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activities, setActivities] = useState([]);
  const [managers, setManagers] = useState([]);
  const [managerId, setManagerId] = useState("");
  const [userIds, setUserIds] = useState([]);

  // "ALL" is the "All Teams" sentinel from the sidebar selector — no filtering then.
  const isScoped = businessUnit?._id && businessUnit._id !== "ALL";

  const getUsers = async () => {
    try {
      const query = {
        role: ["super_admin", "admin", "user"],
        contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"],
        status: "active",
      };
      if (isScoped) query.business_unit_id = businessUnit._id;
      const { ok, data } = await api.post(`/user/search`, query);
      const _managers = data.users.filter((u) => data.users.some((otherUser) => otherUser.manager_id === u._id));
      setManagers(_managers);
      setUserIds(data.users.map((u) => u._id));
    } catch (error) {
      console.error(error);
      toast.error("Error while fetching users");
    }
  };

  useEffect(() => {
    getUsers();
  }, [businessUnit?._id]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!date || userIds.length === 0) return;
    load();
  }, [date, contract, managerId, userIds]);

  useEffect(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const date = params.get("date");
    if (date) setDate(new Date(date));
  }, []);

  async function load() {
    setLoading(true);
    const url = `/activity?date=${date?.getTime()}&userContract=${contract}&manager_id=${managerId}`;
    const { data: allData } = await api.get(url);
    // Only show activities of people from the selected business unit
    const data = isScoped ? allData.filter((a) => userIds.includes(a.userId)) : allData;
    setLoading(false);

    let arr = [];

    for (let i = 0; i < data.length; i++) {
      const found = arr.find((e) => e.userId === data[i].userId);
      if (!found) {
        const obj = { userId: data[i].userId, detail: data[i].detail.map((e) => e.value), userName: data[i].userName, total: data[i].total, userContract: data[i].userContract };
        obj.unavailable = 0;
        obj.holidays = 0;
        obj.rtt = 0;
        obj.arret_maladie = 0;
        if (data[i].projectName === "Unavailable") obj.unavailable = data[i].total || 0;
        if (data[i].projectName === "Leave paid") obj.holidays = data[i].total || 0;
        if (data[i].projectName === "RTT") obj.rtt = data[i].total || 0;
        if (data[i].projectName === "Arret Maladie") obj.arret_maladie = data[i].total || 0;
        arr.push(obj);
      } else {
        if (data[i].projectName === "Unavailable") found.unavailable += data[i].total;
        if (data[i].projectName === "Leave paid") found.holidays += data[i].total;
        if (data[i].projectName === "RTT") found.rtt += data[i].total;
        if (data[i].projectName === "Arret Maladie") found.arret_maladie += data[i].total;
        found.total += data[i].total;
        for (let j = 0; j < data[i].detail.length; j++) {
          found.detail[j] += data[i].detail[j].value;
        }
      }
    }

    arr.sort((a, b) => a.total - b.total);
    setActivities(arr);
  }

  const handleExport = async () => {
    setExporting(true);
    const arr = [];
    for (let activity of activities) {
      const obj = { ...activity };

      const { data: user } = await api.get(`/user/${activity.userId}`);
      obj.bsn = user.bsn;
      obj.salary_comment = user.salary_comment;
      obj.job_title = user.job_title;
      if (user.starting_date) obj.starting_date = new Date(user.starting_date).toISOString().slice(0, 10);

      obj.holidays = activity.holidays / 8;
      obj.unavailable = activity.unavailable / 8;
      obj.rtt = activity.rtt / 8;
      obj.arret_maladie = activity.arret_maladie / 8;
      obj.daysWorked = activity.total / 8;
      if (activity.unavailable || activity.holidays) obj.daysWorked = (activity.total - ((activity?.unavailable || 0) + (activity?.holidays || 0))) / 8;
      if (activity.total) obj.daysWorked = obj.daysWorked.toFixed(1);
      if (activity.unavailable) obj.unavailable = obj.unavailable.toFixed(1);
      if (activity.holidays) {
        obj.holidays = obj.holidays.toFixed(1);
        obj.holiday_details = await getDays(obj, encodeURIComponent("Leave paid"));
      }
      if (activity.rtt) {
        obj.rtt = obj.rtt.toFixed(1);
        obj.rtt_details = await getDays(obj, "RTT");
      }
      if (activity.arret_maladie) {
        obj.arret_maladie = obj.arret_maladie.toFixed(1);
        obj.arret_maladie_details = await getDays(obj, "Arret Maladie");
      }
      arr.push(obj);
    }
    await exportData(arr);
    setExporting(false);
  };

  async function getDays(activity, project) {
    const dateFrom = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
    const dateTo = new Date(dateFrom);
    dateTo.setMonth(dateTo.getMonth() + 1);
    dateTo.setDate(0);

    const { data } = await api.get(`/activity?userId=${activity.userId}&projectName=${project}&dateFrom=${dateFrom.getTime()}&dateTo=${dateTo.getTime()}`);

    let ranges = "";

    for (let i = 0; i < data.length; i++) {
      const slots = [];
      for (let j = 0; j < data[i].detail.length; j++) {
        if (data[i].detail[j].value === 0) continue;
        const slotDate = new Date(data[i].date);
        slotDate.setDate(j + 1);
        if (slotDate < date) continue; // skip past days
        slots.push(slotDate.toISOString().slice(0, 10));
      }
      if (slots.length == 0) continue;

      ranges = slots.sort((a, b) => a - b);
    }

    if (!ranges || !ranges.length) return "";

    return ranges.join(", ");
  }

  const days = getDaysInMonth(date?.getMonth(), date?.getFullYear());
  const getTotal = () => (activities.reduce((acc, a) => acc + a.total, 0) / 8).toFixed(2);

  return (
    <div className="bg-white rounded-xl overflow-hidden w-screen md:w-full shadow-sm">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
        <SelectMonth start={-11} indexDefaultValue={11} value={date} onChange={(e) => setDate(e.target.value)} showArrows />
        <SelectContact value={contract} onChange={(contract) => setContarct(contract)} />
        <select
          className="w-[180px] bg-gray-50 text-sm font-normal py-2 px-3 rounded-lg border border-gray-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors"
          value={managerId}
          onChange={(e) => setManagerId(e.target.value)}>
          <option value={""}>All managers</option>
          {managers.map((manager) => (
            <option value={manager._id}>{manager.name}</option>
          ))}
        </select>
        <LoadingButton loading={exporting} className="btn btn-primary self-start" onClick={handleExport}>
          Export
        </LoadingButton>
      </div>

      <div className="p-4 md:px-6">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400 text-sm">Loading activities...</div>
        ) : (
          <div className="rounded-lg overflow-auto">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-2 text-[11px] font-semibold text-gray-400 text-left pl-4 uppercase tracking-wider">Users</th>
                    {days.map((e) => {
                      const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
                      const _date = new Date(e);
                      const day = _date.getDay();
                      const weekday = days[day];
                      const date = _date.getDate();
                      const isWeekend = day == 0 || day == 6;
                      return (
                        <th className="w-[36px] text-center py-1.5" key={e} day={day}>
                          <div className={`text-[10px] font-medium ${isWeekend ? "text-rose-400" : "text-gray-400"} uppercase`}>{weekday}</div>
                          <div className={`text-[13px] font-bold ${isWeekend ? "text-rose-500" : "text-gray-700"}`}>{date}</div>
                        </th>
                      );
                    })}
                    <th className="w-[36px]" />
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-2">
                      <div className="flex justify-end w-full text-[13px] font-bold text-gray-800">
                        {`${getTotal()} days`}
                      </div>
                    </th>
                  </tr>
                  {activities.map((e, i) => {
                    return (
                      <React.Fragment key={e.projectId}>
                        <tr key={`${e.userName}-${i}`} className="border-b border-gray-100 hover:bg-[#faf8f5]/60 transition-colors">
                          <th className="w-[100px] text-[13px] font-bold text-gray-800 text-left">
                            <div className="flex flex-1 items-center justify-between gap-2 px-4 py-1.5">
                              <button
                                className="flex flex-1 items-center justify-start gap-1.5 hover:text-blue-600 transition-colors"
                                onClick={() => navigate(`/activity?username=${e.userName}&user_id=${e.userId}&date=${date.toISOString().split("T")[0]}`)}>
                                <div className="flex-1 text-left truncate">{e.userName}</div>
                                <HiArrowTopRightOnSquare className="w-3 h-3 opacity-40" />
                              </button>
                              <div className="text-[11px] text-gray-400 font-medium tabular-nums whitespace-nowrap">{(e.total / 8).toFixed(1)}d</div>
                            </div>
                          </th>
                          {e.detail.map((e, i) => {
                            return <Field key={`day-${i}`} value={e} disabled />;
                          })}
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ value = 0, onChange, invoiced, ...rest }) => {
  let badgeBg = "";
  let badgeText = "";

  if (value >= 7) {
    badgeBg = "bg-[#2d6a4f]";
    badgeText = "text-white";
  } else if (value >= 5) {
    badgeBg = "bg-[#40916c]";
    badgeText = "text-white";
  } else if (value >= 3) {
    badgeBg = "bg-[#52b788]";
    badgeText = "text-white";
  } else if (value > 0) {
    badgeBg = "bg-[#95d5b2]";
    badgeText = "text-[#1b4332]";
  }

  if (value === 0 || !value) {
    return (
      <td className="py-1 px-0 text-center w-[36px]">
        <span className="text-[12px] text-gray-300 tabular-nums">0</span>
      </td>
    );
  }

  return (
    <td className="py-1 px-[2px] text-center w-[36px]">
      <div className={`w-[30px] h-[28px] flex items-center justify-center ${badgeBg} ${badgeText} rounded-md text-[12px] font-bold tabular-nums mx-auto`}>
        {value}
      </div>
    </td>
  );
};

export default Activity;

function getDaysInMonth(month, year) {
  var date = new Date(year, month, 1);
  date.setHours(0, 0, 0, 0);
  var days = [];
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
}

async function exportData(entities) {
  if (!entities.length) return;

  const fileName = "users_availablity-" + new Date().toISOString();
  const customColumns = [
    "userName",
    "job_title",
    "daysWorked",
    "holidays",
    "holiday_details",
    "rtt",
    "rtt_details",
    "arret_maladie",
    "arret_maladie_details",
    "unavailable",
    "userContract",
    "bsn",
    "salary_comment",
    "starting_date",
  ];

  const columns = customColumns;

  const csv = [];

  for (let j = 0; j < entities.length; j++) {
    let obj = {};
    for (let i = 0; i < columns.length; i++) {
      let value = entities[j][customColumns[i]];
      if (!value) value = "";
      obj = { ...obj, [customColumns[i]]: value };
    }
    csv.push(obj);
  }
  exportCSVFile(columns, csv, fileName);
}

function exportCSVFile(headers, items, fileTitle) {
  if (headers) {
    items.unshift(headers);
  }

  // Convert Object to JSON
  var jsonObject = JSON.stringify(items);

  var csv = convertToCSV(jsonObject);

  var exportedFilenmae = fileTitle + ".csv" || "export.csv";

  var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, exportedFilenmae);
  } else {
    var link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", exportedFilenmae);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

function convertToCSV(objArray) {
  var array = typeof objArray != "object" ? JSON.parse(objArray) : objArray;
  var str = "";

  for (var i = 0; i < array.length; i++) {
    var line = "";
    for (var index in array[i]) {
      if (line != "") line += ";";

      line += array[i][index];
    }

    str += line + "\r\n";
  }

  return str;
}
